import React, { useEffect } from 'react';
import { createConfetti } from '../../utils/confetti';
import { ScoreDetail } from '../../types/game';
import { useTheme } from '../../contexts/ThemeContext';

interface RevealScreenProps {
  correctAnswer: string;
  evidence?: string;
  secondsLeft?: number;
  wasCorrect: boolean;
  scoreDetail?: ScoreDetail;
}

const RevealScreen: React.FC<RevealScreenProps> = ({ correctAnswer, evidence, secondsLeft = 10, wasCorrect, scoreDetail }) => {
  const { theme } = useTheme();
  const [countdown, setCountdown] = React.useState(secondsLeft);

  useEffect(() => {
    setCountdown(secondsLeft);
    const timer = setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsLeft]);

  useEffect(() => {
    if (wasCorrect) {
      createConfetti();
    }
  }, [wasCorrect]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 animate-[fadeIn_0.3s_ease-in]">
      <div
        className="rounded-3xl p-8 max-w-2xl w-full shadow-2xl text-center backdrop-blur-md"
        style={{ backgroundColor: theme.colors.cardBackground }}
      >
        {wasCorrect ? (
          <>
            <div className="text-6xl mb-4 animate-bounce">🎉</div>
            <h2
              className="text-5xl font-bold mb-4"
              style={{ color: theme.colors.correct }}
            >
              Hurray! 🎊
            </h2>
            <p
              className="text-2xl font-semibold mb-6"
              style={{ color: theme.colors.correct }}
            >
              You got it right!
            </p>
          </>
        ) : (
          <>
            <div className="text-6xl mb-4">😢</div>
            <h2
              className="text-4xl font-bold mb-4"
              style={{ color: theme.colors.textPrimary }}
            >
              Oops!
            </h2>
            <p
              className="text-xl mb-6"
              style={{ color: theme.colors.textSecondary }}
            >
              Better luck next time!
            </p>
          </>
        )}

        {/* Score Breakdown */}
        {scoreDetail && (
          <div
            className="mb-6 rounded-2xl p-6 border-2"
            style={{
              backgroundColor: `${theme.colors.accent}20`,
              borderColor: `${theme.colors.accent}50`
            }}
          >
            <h3
              className="text-xl font-bold mb-4"
              style={{ color: theme.colors.textPrimary }}
            >
              Your Performance
            </h3>
            <div className="space-y-3">
              {scoreDetail.wasCorrect ? (
                <>
                  <div
                    className="flex items-center justify-between font-semibold"
                    style={{ color: theme.colors.correct }}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-2xl">✓</span>
                      <span>Correct Answer!</span>
                    </span>
                    <span>+{scoreDetail.basePoints} pts</span>
                  </div>
                  <div
                    className="flex items-center justify-between font-semibold"
                    style={{ color: theme.colors.textPrimary }}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-2xl">⚡</span>
                      <span>Speed Bonus (answered in {scoreDetail.answerTimeSeconds}s)</span>
                    </span>
                    <span>+{scoreDetail.timeBonus} pts</span>
                  </div>
                  <div
                    className="flex items-center justify-between font-bold text-lg pt-2 border-t-2"
                    style={{
                      color: theme.colors.textPrimary,
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    }}
                  >
                    <span>Total Earned</span>
                    <span style={{ color: theme.colors.accent }}>+{scoreDetail.pointsEarned} pts</span>
                  </div>
                </>
              ) : (
                <div
                  className="flex items-center justify-center font-semibold text-lg"
                  style={{ color: theme.colors.incorrect }}
                >
                  <span className="text-2xl mr-2">✗</span>
                  <span>Wrong Answer - No points this round</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div
          className="rounded-2xl p-6 mb-6"
          style={{
            background: wasCorrect ? theme.colors.correct : theme.colors.incorrect,
            color: '#fff'
          }}
        >
          <p className="text-sm font-semibold mb-2 opacity-80">Correct Answer:</p>
          <div className="text-2xl font-bold mb-2">
            {correctAnswer}
          </div>
          {evidence && (
            <p className="text-lg opacity-90">{evidence}</p>
          )}
        </div>
        <div className="animate-pulse text-2xl mb-2">⏳</div>
        <p
          className="font-medium"
          style={{ color: theme.colors.textSecondary }}
        >
          Next question in <span className="font-bold" style={{ color: theme.colors.accent }}>{countdown}</span> seconds...
        </p>
      </div>
    </div>
  );
};

export default RevealScreen;